import SEO from '../pages/SEO';
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Calendar, MapPin, Stethoscope } from "lucide-react";
import MedicalCamps from '../images/MedicalCamps.jpg'


export default function CampsPage() {
  const benefits = [
    "Free or subsidized orthopaedic consultations",
    "Early detection of bone and joint problems",
    "Health education and awareness programs",
    "On-site diagnostic services and screening",
    "Referral and treatment planning assistance",
    "Community outreach and accessibility",
  ];

  const campTypes = [
    {
      title: "Orthopaedic Screening Camps",
      description:
        "Comprehensive joint and bone health assessments for early detection of arthritis, osteoporosis, and musculoskeletal disorders.",
      icon: Stethoscope,
    },
    {
      title: "Sports Injury Prevention Camps",
      description:
        "Educational programs for athletes, coaches, and sports enthusiasts on injury prevention, proper training techniques, and biomechanics.",
      icon: Users,
    },
    {
      title: "Geriatric Bone Health Camps",
      description:
        "Specialized camps for seniors focusing on fall prevention, osteoporosis screening, and mobility assessments.",
      icon: Users,
    },
    {
      title: "Corporate Wellness Programs",
      description:
        "On-site workplace health camps addressing posture-related issues, ergonomic assessments, and desk-related musculoskeletal problems.",
      icon: Calendar,
    },
  ];

  const pastCamps = [
    {
      title: "Community Health Camp - Hyderabad",
      date: "January 2025",
      location: "Kukatpally Community Hall",
      beneficiaries: "200+ people screened",
    },
    {
      title: "School Sports Injury Prevention Workshop",
      date: "December 2024",
      location: "DPS School, Secunderbad",
      beneficiaries: "150 students and coaches",
    },
    {
      title: "Senior Citizen Bone Health Camp",
      date: "November 2024",
      location: "Senior Citizens Association, Banjara Hills",
      beneficiaries: "120 seniors assessed",
    },
    {
      title: "Corporate Wellness Initiative",
      date: "October 2024",
      location: "Tech Park, HITEC City",
      beneficiaries: "300+ employees",
    },
  ];

  const services = [
    "General orthopaedic consultation",
    "Joint pain and mobility assessment",
    "Posture and gait analysis",
    "Bone density screening (when applicable)",
    "Basic X-ray review and interpretation",
    "Treatment recommendations and referrals",
    "Physiotherapy guidance",
    "Nutritional counseling for bone health",
  ];

  const faqs = [
    {
      question: "Are the medical camps free?",
      answer:
        "Most of our community health camps offer free consultations and basic screening services. Some specialized diagnostic tests may be available at subsidized rates. Corporate and institutional camps are tailored based on the organization's requirements.",
    },
    {
      question: "How can I register for an upcoming camp?",
      answer:
        "You can register through our website, call our clinic directly, or register on-site at the camp venue (subject to availability). We announce upcoming camps on our social media channels and website.",
    },
    {
      question: "What should I bring to a health camp?",
      answer:
        "Please bring any previous medical records, X-rays, or reports related to your orthopaedic condition. Also bring a list of current medications and any questions you'd like to discuss with the doctor.",
    },
    {
      question: "Can I request a camp for my organization or community?",
      answer:
        "Yes! We organize customized health camps for schools, colleges, corporates, residential communities, and NGOs. Contact us to discuss your requirements, and we'll work with you to plan an effective health camp.",
    },
    {
      question: "What happens after the camp screening?",
      answer:
        "If any issues are detected during screening, you'll receive recommendations for further evaluation, treatment options, and follow-up care. Patients requiring specialized treatment will be given priority appointments at our clinic.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Orthopedic Health Camps in Hyderabad | Dr. Sujit Kumar"
        description="Learn about orthopedic screening and community health camps organized by Dr. Sujit Kumar, including bone health and sports injury prevention in Hyderabad."
        keywords="orthopedic health camp Hyderabad, bone health camp, sports injury prevention camp, Dr Sujit Kumar"
        canonical="https://www.drsujitkumar.com/treatments/medical-camps"
        robots="index, follow"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Medical Camps
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Community health initiatives for orthopaedic screening, awareness, and early detection
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p>
                  We believe in preventive healthcare and making quality orthopaedic care accessible to all. Through our
                  regular medical camps and community outreach programs, we aim to educate, screen, and provide early
                  intervention for bone and joint health issues.
                </p>
                <p>
                  Our camps serve diverse communities including schools, corporate organizations, senior citizen groups,
                  sports clubs, and underserved areas. We focus on health education, early detection, and connecting
                  patients with appropriate treatment pathways.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Camp Benefits</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src= {MedicalCamps}
                alt="Medical camp community screening"
                className="object-cover w-full h-full"
              />
            </div>
          </div> 
        </div>
      </section>

      {/* Types of Camps */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Types of Camps We Organize</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-5xl mx-auto">
            {campTypes.map((camp) => (
              <Card key={camp.title} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <camp.icon className="h-8 w-8 text-primary" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{camp.title}</h3>
                      <p className="text-muted-foreground">{camp.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Services Offered at Camps</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted/30">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                <span>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Camps */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Past Camps & Initiatives</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-5xl mx-auto">
            {pastCamps.map((camp, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{camp.title}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span>{camp.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>{camp.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4 text-primary" />
                      <span>{camp.beneficiaries}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Request a Health Camp for Your Organization</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Interested in organizing a health camp for your school, company, or community? Contact us to discuss your requirements.
          </p>

          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button size="lg" variant="secondary" className="bg-primary-foreground text-primary" asChild>
              <Link href="/contact">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <Button size="lg" variant="outline" className="bg-transparent border-secondary-foreground text-secondary-foreground">
              <a href="tel:+917995918882">Call to Inquire</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
